/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { generatePath, Link, useHistory } from "react-router-dom";
import { ExtractRouteParams } from "react-router";
import * as React from "react";
import { useEffect } from "react";
import { MouseEventHandler, useCallback } from "react";
import { match, P } from "ts-pattern";
import { useCurrentUser } from "actions/CurrentUserActions";
import { useLocale } from "components/LocaleContext";
import { trackPage } from "api/CustomerPersonalization";

interface BasePageOptions<S extends string> {
  absolute?: boolean;
  locale?: "none" | string;
  pathParams?: ExtractRouteParams<S>;
}

export const usePageUrl = <S extends string>(
  to: S,
  options?: BasePageOptions<S>
): string => {
  return useGeneratePageUrl()(to, options);
};

export const useGeneratePageUrl = (): (<S extends string>(
  to: S,
  options?: BasePageOptions<S>
) => string) => {
  const currentLocale = useLocale();
  const history = useHistory();

  return useCallback(
    (to: string, options) => {
      const locale = options?.locale ?? currentLocale;
      const baseUrl = generatePath(to, options?.pathParams);

      const localeUrl = match([locale, baseUrl])
        .with(["none", P.any], () => baseUrl)
        .with([P.any, "/"], () => `/${locale}`)
        .otherwise(
          () => `/${locale}${baseUrl.startsWith("/") ? baseUrl : `/${baseUrl}`}`
        );

      if (options?.absolute) {
        const prefixUrl = history.createHref({ pathname: localeUrl });
        const origin =
          navigator.userAgent === "ReactSnap"
            ? process.env.CDN_ROOT
            : window.location.origin;

        return `${origin}${prefixUrl}`.toLowerCase();
      } else {
        return localeUrl.toLowerCase();
      }
    },
    [currentLocale, history]
  );
};

export const useHistoryPush = (): (<S extends string>(
  to: S,
  options?: BasePageOptions<S> & { requireAuthentication?: boolean }
) => void) => {
  const history = useHistory();
  const localizePath = useGeneratePageUrl();
  const { currentUser } = useCurrentUser();

  return useCallback(
    (to, options) => {
      if (options?.requireAuthentication && !currentUser) {
        window.adobeIMS?.signIn({
          redirect_uri: localizePath(to, { ...options, absolute: true }),
        });
      } else {
        history.push(localizePath(to, options));
      }
    },
    [currentUser, history, localizePath]
  );
};

export const useHistoryReplace = (): (<S extends string>(
  to: S,
  options?: BasePageOptions<S>
) => void) => {
  const history = useHistory();
  const localizePath = useGeneratePageUrl();

  return useCallback(
    (to, options) => {
      history.replace(localizePath(to, options));
    },
    [history, localizePath]
  );
};

export const LocaleLink = <S extends string>({
  to,
  children,
  className,
  ariaLabel,
  ...props
}: React.PropsWithChildren<
  BasePageOptions<S> & {
    to: S;
    requireAuthentication?: boolean;
    className?: string;
    ariaLabel?: string;
    "data-testid"?: string;
  }
>): ReturnType<React.FC> => {
  const localizedTo = usePageUrl(to, props);
  const push = useHistoryPush();
  const history = useHistory();

  const authenticatedClick: MouseEventHandler = useCallback(
    (event) => {
      event.preventDefault();
      push(to, props);
    },
    [props, push, to]
  );

  if (props.requireAuthentication) {
    return (
      <a
        className={className}
        href={history.createHref({ pathname: localizedTo })}
        onClick={authenticatedClick}
        aria-label={ariaLabel}
        data-testid={props["data-testid"]}
      >
        {children}
      </a>
    );
  }

  // Don't modify absolute URLs or external links
  if (to.startsWith("http") || to.startsWith("//")) {
    return (
      <a
        className={className}
        href={to}
        data-testid={props["data-testid"]}
        aria-label={ariaLabel}
      >
        {children}
      </a>
    );
  }

  // For internal links, prepend locale
  return (
    <Link
      className={className}
      to={localizedTo}
      data-testid={props["data-testid"]}
      aria-label={ariaLabel}
    >
      {children}
    </Link>
  );
};

export const usePageTracker = (): void => {
  const history = useHistory();

  useEffect((): void | VoidFunction => {
    const unlisten = history.listen((location, action) => {
      trackPage();
    });

    return () => unlisten();
  }, [history]);
};
